import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { Helper } from "src/app/core/helpers/helper";
import { Provider } from "../../models/provider.model";
import { ServicesService } from "../../services/services.service";
import { MainCategory } from "../../../q-commerce/models/main-category.model";

@Component({
  selector: "app-service-provider-modal",
  imports: [CommonModule, TranslateModule],
  templateUrl: "./service-provider-modal.component.html",
  styleUrl: "./service-provider-modal.component.scss"
})
export class ServiceProviderModalComponent {
  providers: Provider[] = [];
  isIndividualProvider: boolean = true;
  cartServiceItemMainCategory!: MainCategory;
  destinationAddress!: any;
  citySetting!: any;
  bookingType!: number;
  products = [];
  selectedSortBy: "price" | "ratings" | null = null;

  @Output() onSelectProvider = new EventEmitter<{ typeId: string; entityType: number }>();
  @ViewChild("serviceProviderModal", { static: true }) serviceProviderModal!: TemplateRef<any>;

  constructor(private offcanvasService: NgbOffcanvas, public helper: Helper, private servicesService: ServicesService) {}

  openServiceProvidersModal(bookingType: number): void {
    this.offcanvasService.open(this.serviceProviderModal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" });
    this.isIndividualProvider = true;
    this.cartServiceItemMainCategory = localStorage.getItem("cartServiceItemMainCategory")
      ? JSON.parse(localStorage.getItem("cartServiceItemMainCategory") as string)
      : ({} as MainCategory);
    this.bookingType = bookingType;
    this.destinationAddress = JSON.parse(localStorage.getItem("currentDeliveryAddress") || "{}");
    this.citySetting = JSON.parse(localStorage.getItem("citySetting") || "{}");
    const cartItems = JSON.parse(localStorage.getItem("serviceCartItems") || "[]");
    this.products = cartItems.map((product: any) => ({
      ...product,
      mainCategoryId: this.cartServiceItemMainCategory._id,
      serviceSetting: this.cartServiceItemMainCategory.serviceSetting
    }));
    if (this.cartServiceItemMainCategory.serviceSetting?.isAllowServiceProviderSelection) {
      this.getIndividualProviders();
    } else {
      this.isIndividualProvider = false;
      this.getCompanyProviders();
    }
  }

  /**
   * Fetches the list of individual service providers based on the specified parameters.
   * Constructs a parameter object containing destination address, main category ID, city ID, timezone, booking type, and products.
   * Calls the ServicesService to get the service providers and updates the component's providers list if drivers are received in the response.
   */
  getIndividualProviders(): void {
    const params: any = {
      destinationAddress: this.destinationAddress,
      mainCategoryId: this.cartServiceItemMainCategory._id,
      cityId: this.citySetting.cityId,
      timezone: this.citySetting.timezone,
      bookingType: this.bookingType,
      products: this.products,
      isShowErrorToast: false,
      isShowSuccessToast: false
    };
    if (this.selectedSortBy === "ratings") {
      params.sortField = "rate";
      params.sortOrder = -1;
    } else if (this.selectedSortBy === "price") {
      params.sortField = "totalDiscountedPrice";
      params.sortOrder = 1;
    }
    this.servicesService.getServiceProviders(params).subscribe((response) => {
      if (response && response.drivers) {
        this.providers = response.drivers;
      } else {
        this.providers = [];
      }
    });
  }

  /**
   * Fetches the list of service providers using the ApiService.
   * Subscribes to the service's observable to receive the provider data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the provider data.
   */
  getCompanyProviders(): void {
    const params: any = {
      destinationAddress: this.destinationAddress,
      mainCategoryId: this.cartServiceItemMainCategory._id,
      cityId: this.citySetting.cityId,
      timezone: this.citySetting.timezone,
      bookingType: this.bookingType,
      products: this.products,
      isShowErrorToast: false,
      isShowSuccessToast: false
    };
    if (this.selectedSortBy === "ratings") {
      params.sortField = "customerRate";
      params.sortOrder = -1;
    } else if (this.selectedSortBy === "price") {
      params.sortField = "totalDiscountedPrice";
      params.sortOrder = 1;
    }
    this.servicesService.getMerchantList(params).subscribe((response) => {
      if (response && response.merchants) {
        this.providers = response.merchants;
      } else {
        this.providers = [];
      }
    });
  }

  toggleIndividuals(): void {
    this.isIndividualProvider = true;
    this.selectedSortBy = null;
    this.getIndividualProviders();
  }

  toggleCompanies(): void {
    this.isIndividualProvider = false;
    this.selectedSortBy = null;
    this.getCompanyProviders();
  }

  /**
   * Handles image loading errors by setting a default image.
   *
   * @param event The event object containing the error details.
   */
  handleImageError(event: Event): void {
    (event.target as HTMLImageElement).src = "assets/images/default-image.jpg";
  }

  onSortChange(type: "price" | "ratings"): void {
    this.selectedSortBy = this.selectedSortBy === type ? null : type;
    if (this.isIndividualProvider) {
      this.getIndividualProviders();
    } else {
      this.getCompanyProviders();
    }
  }

  selectProvider(provider: any): void {
    this.offcanvasService.dismiss(this.serviceProviderModal);
    this.onSelectProvider.emit({ typeId: provider._id, entityType: this.isIndividualProvider ? this.helper.UserType.DRIVER : this.helper.UserType.MERCHANT });
  }

  /**
   * Closes the service providers modal.
   */
  closeModal(): void {
    this.offcanvasService.dismiss(this.serviceProviderModal);
  }
}
